/**
 * @file Config management for RxDB state-based configs
 * This composable handles reactive config creation with two-phase initialization.
 * @since 2025-10-15
 */

const { ref, computed } = Vue;
import { useErrorHandler } from '../../core/errors.js';
import { DatabaseError } from '../../utils/errors.js';
import {
  applySchemaDefaults,
  validateSchemaValue,
} from '../../json-editor/schemas.js';
import { useDb } from '../../core/db.js';
import { conflictHandlerWithStrategies } from '../../merge/index.js';

const db = useDb();

// Global registry of all configs
const _registry = new Map();

/**
 * Get all registered configs
 * @returns {Array} Array of config objects
 */
export function _getRegisteredConfigs() {
  return Array.from(_registry.values());
}

/**
 * Get a config by name
 * @param {string} configName - Name of the config
 * @returns {object} Config object
 * @throws {Error} If config not found
 */
export function getConfig(configName) {
  const config = _registry.get(configName);
  if (!config) {
    throw new Error(`Config '${configName}' not found`);
  }
  return config;
}

/**
 * Create and return a reactive config object
 *
 * @param {object} configDef - Config definition
 * @param {string} configDef.name - Unique config identifier
 * @param {any} configDef.defaultValue - Initial value (object/array/primitive)
 * @param {object} [configDef.schema] - Optional JSON schema for validation
 * @param {object} [configDef.conflictHandler] - Optional conflict handler with mergeStrategies and defaultStrategy
 * @param {Function} [configDef.setupReplication] - Optional replication setup function
 * @returns {object} Reactive config object
 */
export function useConfig(configDef) {
  if (!configDef || !configDef.name) {
    throw new Error('Config definition must have a name');
  }
  if (configDef.defaultValue === undefined) {
    throw new Error(`Config '${configDef.name}' must have a defaultValue`);
  }

  const configName = configDef.name;

  // Return existing config if already registered
  if (_registry.has(configName)) {
    return _registry.get(configName);
  }

  // Create promise that resolves when init process is complete
  let initPromiseResolve = null;
  const initPromise = new Promise(resolve => {
    initPromiseResolve = resolve;
  });

  // Populated once db is initialized
  let state = null;
  let replicationHandler = null;
  let syncTimer = null;
  let syncDebounceTimer = null;
  let ready = false;

  // Use provided conflict handler or create default one
  const conflictHandler =
    configDef.conflictHandler || conflictHandlerWithStrategies({});

  // Reactive state
  const loading = ref(true);
  const error = ref(null);
  const lastSuccessfulSync = ref(null);
  const lastSyncError = ref(null);
  const stateData = ref({
    value: configDef.defaultValue,
    savedValue: configDef.defaultValue,
    ancestorValue: null,
    checkpoint: null,
  });

  /**
   * Pull from backend and merge with local state
   * @param {object} currentState - Current state object
   * @returns {Promise<object>} Same object if unchanged, or new state with merged values
   * @private
   */
  const pullAndMerge = async currentState => {
    if (!replicationHandler?.pull) {
      return currentState;
    }

    const pullResult = await replicationHandler.pull(currentState.checkpoint);

    // No changes on server
    if (!pullResult || pullResult.data === undefined) {
      return currentState;
    }

    const serverValue = pullResult.data;
    const ancestor = currentState.ancestorValue ?? configDef.defaultValue;

    // No local changes, use server value directly
    if (
      conflictHandler.isEqual(currentState.value, ancestor) &&
      conflictHandler.isEqual(currentState.savedValue, ancestor)
    ) {
      return {
        value: serverValue,
        savedValue: serverValue,
        ancestorValue: serverValue,
        checkpoint: pullResult.checkpoint,
      };
    }

    // Merge server changes into value
    const mergedValue = conflictHandler.resolve({
      newDocumentState: currentState.value,
      realMasterState: serverValue,
      assumedMasterState: ancestor,
    });

    // Merge server changes into savedValue
    const mergedSavedValue = conflictHandler.resolve({
      newDocumentState: currentState.savedValue,
      realMasterState: serverValue,
      assumedMasterState: ancestor,
    });

    return {
      value: mergedValue,
      savedValue: mergedSavedValue,
      ancestorValue: serverValue,
      checkpoint: pullResult.checkpoint,
    };
  };

  /**
   * Push savedValue to backend if it differs from ancestorValue
   * @param {object} currentState - Current state object
   * @returns {Promise<object>} Same object if no push needed, or new state after push
   * @private
   */
  const pushIfNeeded = async currentState => {
    if (!replicationHandler?.push) {
      return currentState;
    }

    // Nothing to push if savedValue matches ancestorValue
    if (
      conflictHandler.isEqual(
        currentState.savedValue,
        currentState.ancestorValue
      )
    ) {
      return currentState;
    }

    // Validate before push
    const validationErrors = validateSchemaValue(
      currentState.savedValue,
      config.schema
    );
    if (validationErrors.length > 0) {
      throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
    }

    // Push to backend
    const newCheckpoint = await replicationHandler.push(
      currentState.savedValue,
      currentState.checkpoint
    );

    return {
      ...currentState,
      ancestorValue: currentState.savedValue,
      checkpoint: newCheckpoint,
    };
  };

  // Create reactive config object
  const config = Vue.reactive({
    // Immediate access (Phase 1)
    name: configDef.name,
    schema: configDef.schema || null,
    defaultValue: configDef.defaultValue,
    replicationHandler: null,

    value: computed(() => stateData.value.value),
    savedValue: computed(() => stateData.value.savedValue),
    ancestorValue: computed(() => stateData.value.ancestorValue),

    // Status properties
    loading,
    error,
    lastSuccessfulSync,
    lastSyncErrorMessage: computed(() => lastSyncError.value),
    isModified: computed(
      () =>
        !conflictHandler.isEqual(
          stateData.value.value,
          stateData.value.savedValue
        )
    ),
    isPendingSync: computed(() => {
      const { savedValue, ancestorValue } = stateData.value;
      if (ancestorValue === null) return true; // Never synced
      return !conflictHandler.isEqual(savedValue, ancestorValue);
    }),
    isInSync: computed(() => {
      if (lastSyncError.value) return false;
      const { savedValue, ancestorValue } = stateData.value;
      if (ancestorValue === null) return false; // Never synced
      return conflictHandler.isEqual(savedValue, ancestorValue);
    }),

    /**
     * Get a value at a specific path (reactive computed)
     * @param {string} path - Dot-separated path (e.g., 'theme.color')
     * @returns {any} Value at path
     */
    get: computed(() => path => {
      const pathParts = path.split('.');
      let current = stateData.value.value;

      for (const part of pathParts) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          return undefined;
        }
      }

      return current;
    }),

    /**
     * Set a value at a specific path
     * @param {string} path - Dot-separated path (e.g., 'theme.color')
     * @param {any} value - Value to set at path
     * @returns {Promise<void>}
     */
    async set(path, value) {
      await initPromise;

      const pathParts = path.split('.');
      const currentValue = stateData.value.value;
      const newValue = JSON.parse(JSON.stringify(currentValue)); // Deep clone
      let current = newValue;

      // Navigate to parent and set value
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!current[part] || typeof current[part] !== 'object') {
          current[part] = {};
        }
        current = current[part];
      }

      current[pathParts[pathParts.length - 1]] = value;

      await config.replace(newValue);
    },

    /**
     * Replace entire config value (local edit, not saved)
     * @param {any} newValue - New config value
     * @returns {Promise<void>}
     */
    async replace(newValue) {
      await initPromise;

      try {
        error.value = null;

        // Validate against schema if defined
        const validationErrors = validateSchemaValue(newValue, config.schema);
        if (validationErrors.length > 0) {
          throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
        }

        // Update only value, preserve rest of state
        const currentState = stateData.value;
        await state.set('value', () => ({
          ...currentState,
          value: newValue,
        }));
      } catch (err) {
        error.value = err;
        throw err;
      }
    },

    /**
     * Reset config to default value
     * @returns {Promise<void>}
     */
    async reset() {
      await config.replace(configDef.defaultValue);
    },

    /**
     * Sync with backend (pull then push if needed)
     * @returns {Promise<void>}
     */
    async sync() {
      await initPromise;

      if (!replicationHandler) {
        return;
      }

      try {
        error.value = null;
        let currentState = stateData.value;

        // Pull and merge
        currentState = await pullAndMerge(currentState);

        // Push if needed
        currentState = await pushIfNeeded(currentState);

        // Only persist if state actually changed
        if (currentState !== stateData.value) {
          await state.set('value', () => currentState);
        }

        // Update sync status
        lastSuccessfulSync.value = new Date();
        lastSyncError.value = null;
      } catch (err) {
        lastSyncError.value = err.message;
        error.value = err;
        throw err;
      }
    },

    /**
     * Save current value and schedule sync with backend
     * @returns {Promise<void>}
     */
    async save() {
      await initPromise;

      const currentState = stateData.value;

      // Copy value to savedValue
      await state.set('value', () => ({
        ...currentState,
        savedValue: currentState.value,
      }));

      // Schedule debounced sync if replication configured
      if (replicationHandler) {
        const debounceMs = replicationHandler.syncDebounce ?? 1000;
        if (syncDebounceTimer) clearTimeout(syncDebounceTimer);
        syncDebounceTimer = setTimeout(() => {
          this.sync().catch(err => {
            console.warn(
              `Debounced sync failed for config '${configName}':`,
              err
            );
          });
        }, debounceMs);
      }
    },

    /**
     * Get the underlying RxDB state instance
     * @returns {Promise<object>}
     */
    async getInstance() {
      await initPromise;
      return state;
    },

    /**
     * Bind to RxDB database (auto-called on creation)
     * @private
     */
    async _init() {
      if (ready) return;

      try {
        // Wait for database resolution
        await db.ensureInitialized();
        const rxdb = db.rawDb;

        loading.value = true;

        // Register in database
        db.configs.push(config);

        // Create or get existing state with namespace
        state = await rxdb.addState(configName);

        // Get current value
        const storedValue = state.get('value');

        if (!storedValue) {
          // Initialize with default value
          const initialData = {
            value: configDef.defaultValue,
            savedValue: configDef.defaultValue,
            ancestorValue: null,
            checkpoint: null,
          };
          await state.set('value', () => initialData);
          stateData.value = initialData;
        } else {
          // Apply schema defaults to existing value (for schema evolution)
          const clonedValue = JSON.parse(JSON.stringify(storedValue.value));
          const valueWithDefaults = applySchemaDefaults(
            configDef.schema,
            clonedValue
          );

          // Migrate from old format if needed (support _checkpoint, _ancestorValue)
          const clonedSaved = storedValue.savedValue
            ? JSON.parse(JSON.stringify(storedValue.savedValue))
            : valueWithDefaults;
          const savedWithDefaults = applySchemaDefaults(
            configDef.schema,
            clonedSaved
          );

          stateData.value = {
            value: valueWithDefaults,
            savedValue: savedWithDefaults,
            ancestorValue:
              storedValue.ancestorValue ?? storedValue._ancestorValue ?? null,
            checkpoint:
              storedValue.checkpoint ?? storedValue._checkpoint ?? null,
          };

          // Update stored value if migration or defaults were applied
          if (JSON.stringify(stateData.value) !== JSON.stringify(storedValue)) {
            await state.set('value', () => stateData.value);
          }
        }

        // Subscribe to state changes
        state.get$('value').subscribe(newValue => {
          if (newValue) {
            const clonedValue = JSON.parse(JSON.stringify(newValue.value));
            const valueWithDefaults = applySchemaDefaults(
              configDef.schema,
              clonedValue
            );
            const clonedSaved = JSON.parse(JSON.stringify(newValue.savedValue));
            const savedWithDefaults = applySchemaDefaults(
              configDef.schema,
              clonedSaved
            );
            stateData.value = {
              value: valueWithDefaults,
              savedValue: savedWithDefaults,
              ancestorValue: newValue.ancestorValue ?? null,
              checkpoint: newValue.checkpoint ?? null,
            };
          }
        });

        // Setup replication if configured
        if (configDef.setupReplication) {
          try {
            replicationHandler = await configDef.setupReplication(configName);
            this.replicationHandler = replicationHandler;
          } catch (err) {
            console.warn(
              `Replication setup failed for config '${configName}':`,
              err
            );
          }
        }

        ready = true;
        initPromiseResolve();

        // Initial sync
        if (replicationHandler) {
          this.sync().catch(err => {
            console.warn(
              `Initial sync failed for config '${configName}':`,
              err
            );
          });

          // Setup periodic sync if configured
          if (replicationHandler.pullInterval) {
            syncTimer = setInterval(() => {
              this.sync().catch(err => {
                console.warn(
                  `Periodic sync failed for config '${configName}':`,
                  err
                );
              });
            }, replicationHandler.pullInterval);
          }
        }

        console.log(`Config '${configName}' initialized successfully`);
      } catch (err) {
        const { handleError } = useErrorHandler();
        const dbError = new DatabaseError(
          `Failed initialize config '${configName}': ${err.message}`,
          'configs',
          err
        );
        error.value = dbError;
        await handleError(dbError, {
          operation: 'bindConfig',
          configName,
        });
        throw err;
      } finally {
        loading.value = false;
      }
    },
  });

  // Register config in global registry
  _registry.set(configName, config);

  // Auto-start binding
  config._init();

  return config;
}

/**
 * Default export for convenience
 * @default
 */
export default useConfig;
